import { AddressModel, Country, DataValue, ErrorType, RE_EMAIL, RE_SSN, State } from '@tradingblock/types';
import _ from 'lodash';
import Config from '../../../config';

export interface USPSErrors {
  [key: string]: string | undefined;
}

export const getError = (value: DataValue, error: ErrorType) => {
  if (!value && !_.isNumber(value)) {
    return error;
  }
  return undefined;
};

export const getRequiredError = (value: DataValue) => {
  return getError(value, ErrorType.Required);
};

export const getInvalidError = (value: DataValue) => {
  return getError(value, ErrorType.Invalid);
};

export const getInvalidLengthError = (value: DataValue) => {
  return getError(value, ErrorType.InvalidLength);
};

export const getAmountError = (value: DataValue) => {
  return getError(value, ErrorType.Amount);
};

export const getContingentError = (value: DataValue) => {
  return getError(value, ErrorType.Contingent);
};

export const getMismatchError = (value: DataValue) => {
  return getError(value, ErrorType.Mismatch);
};

export const getEmailError = (value: string | null | undefined) => {
  return getRequiredError(value) || getInvalidError(value && RE_EMAIL.test(value));
};

/** SSN/ITIN: required for individuals; must match `RE_SSN` after trim. */
export const getSsnItinError = (value: string | null | undefined) => {
  const trimmed = (value != null ? String(value) : '').trim();
  return getRequiredError(trimmed) || getInvalidError(RE_SSN.test(trimmed));
};

export const getStaleError = (value: DataValue) => {
  return getError(value, ErrorType.Stale);
};

export const getUploadError = (value: DataValue) => {
  return getError(value, ErrorType.Upload);
};

export const RE_PO_BOX = /^ *((#\d+)|((box|bin)[-. \/\\]?\d+)|(.*p[ \.]? ?(o|0)[-. \/\\]? *-?((box|bin)|b|(#|num)?\d+))|(p(ost)? *(o(ff(ice)?)?)? *((box|bin)|b)? *\d+)|(p *-?\/?(o)? *-?box)|post office box|((box|bin)|b) *(number|num|#)? *\d+|(num|number|#) *\d+)/i;

// USPS Address Verification using new backend REST endpoint
export const uspsVerification = async (values: AddressModel, apiToken?: string) => {
  const tradingApiUrl = Config.tradingApi;
  const { address1, address2, city, state, country, postalCode } = values;

  try {
    if (address1 && city && state && country === Country.UnitedStatesOfAmerica && state !== State.PuertoRico) {
      // Don't make API call without authentication token
      if (!apiToken) {
        console.warn('USPS verification skipped: API token not provided');
        return;
      }

      // Build query parameters with %20 encoding for spaces
      const queryParams: string[] = [];
      queryParams.push(`address1=${encodeURIComponent((address1 || '').trim())}`);
      queryParams.push(`city=${encodeURIComponent((city || '').trim())}`);
      queryParams.push(`state=${encodeURIComponent((state || '').trim())}`);

      // Add address2 if provided
      if (address2) {
        queryParams.push(`address2=${encodeURIComponent(address2.trim())}`);
      }

      const queryString = queryParams.join('&');

      // Build request headers - always include token (we've verified it exists above)
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        Authorization: apiToken,
      };

      const response = await fetch(`${tradingApiUrl}/USPSAddress?${queryString}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        // Handle HTTP errors - use layman-friendly message for users
        return {
          error: {
            number: response.status.toString(),
            source: 'HTTP',
            description: 'We couldn\'t verify this address. Please check your information and try again.',
          },
        };
      }

      const jsonResult: any = await response.json();

      // Check ResponseCode (0 = success, non-zero = error)
      if (jsonResult.ResponseCode !== 0) {
        // Handle different error response formats - use layman-friendly messages
        let errorDescription = 'We couldn\'t verify this address. Please check your information and try again.';
        if (jsonResult.Payload) {
          if (jsonResult.Payload.error) {
            errorDescription = jsonResult.Payload.error;
          } else if (typeof jsonResult.Payload === 'string') {
            errorDescription = jsonResult.Payload;
          } else if (jsonResult.Payload.description) {
            errorDescription = jsonResult.Payload.description;
          } else if (jsonResult.Payload.message) {
            errorDescription = jsonResult.Payload.message;
          }
        } else if (jsonResult.error) {
          errorDescription = typeof jsonResult.error === 'string' ? jsonResult.error : jsonResult.error.description || errorDescription;
        } else if (jsonResult.message) {
          errorDescription = jsonResult.message;
        }
        const responseCodeNumber = jsonResult.ResponseCode ? jsonResult.ResponseCode.toString() : '0';
        return {
          error: {
            number: responseCodeNumber,
            source: 'USPS',
            description: errorDescription,
          },
        };
      }

      const payload = jsonResult.Payload;
      if (!payload) {
        return {
          error: {
            number: '0',
            source: 'USPS',
            description: 'We couldn\'t verify this address. Please try again.',
          },
        };
      }

      // Split postalCode into zip5 and zip4
      let zip5 = '';
      let zip4 = '';
      if (payload.postalCode) {
        const zipParts = payload.postalCode.split('-');
        zip5 = zipParts[0] || '';
        zip4 = zipParts[1] || '';
      }

      // Return the standardized address components
      // Note: New API doesn't return address2 separately, so we set it to null
      return {
        city: payload.city || '',
        state: payload.state || '',
        zip5: zip5,
        zip4: zip4,
        uspsAddress1: payload.address1 || '',
        uspsAddress2: null, // New API doesn't provide address2 separately
        isPoBox: payload.isPoBox || false,
        isSecondaryInformationRequired: payload.isSecondaryInformationRequired || false,
        isSecondaryInformationNotRecognized: payload.isSecondaryInformationNotRecognized || false,
      };
    }
  } catch (e) {
    console.error('USPS verification error:', e);
    return {
      error: {
        number: '0',
        source: 'Network',
        description: e instanceof Error ? e.message : 'Network error occurred',
      },
    };
  }
};
